import { useEffect, useState } from "react";
import { Reveal } from "../components/Reveal";
import { submitContact } from "../lib/api";
import { toast } from "sonner";
import { Instagram, Linkedin, MapPin, Phone, Clock } from "lucide-react";
import { useT } from "../lib/LanguageContext";

const CALENDLY_URL = "https://calendly.com/imarchi032/30min";

const useCalendlyScript = () => {
  useEffect(() => {
    const css = document.createElement("link");
    css.rel = "stylesheet";
    css.href = "https://assets.calendly.com/assets/external/widget.css";
    document.head.appendChild(css);

    const js = document.createElement("script");
    js.src = "https://assets.calendly.com/assets/external/widget.js";
    js.async = true;
    document.body.appendChild(js);

    return () => {
      css.remove();
      js.remove();
    };
  }, []);
};

const Contact = () => {
  const { t, lang } = useT();
  useCalendlyScript();

  const [form, setForm] = useState({
    nom: "",
    email: "",
    telephone: "",
    type_client: t("contact.typePro"),
    secteur: "",
    services: [],
    budget: "",
    source: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);

  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const toggleService = (s) => {
    setForm((f) => ({
      ...f,
      services: f.services.includes(s) ? f.services.filter((x) => x !== s) : [...f.services, s],
    }));
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await submitContact(form);
      toast.success(t("common.successContact"));
      setForm({
        nom: "", email: "", telephone: "", type_client: t("contact.typePro"),
        secteur: "", services: [], budget: "", source: "", message: "",
      });
    } catch {
      toast.error(t("common.errorGeneric"));
    } finally {
      setLoading(false);
    }
  };

  const secteurs = t("contact.secteurs");
  const budgets = t("contact.budgets");
  const sources = t("contact.sources");
  const servicesList = t("contact.services");
  const horaires = t("contact.horaires");

  return (
    <main data-testid="page-contact" className="bg-white flex flex-col">
      <section className="order-1 pt-40 pb-16 px-6 bg-[#FFFBEA]">
        <div className="max-w-7xl mx-auto text-center">
          <Reveal>
            <h1 className="font-serif text-5xl md:text-7xl font-bold text-[#6B5CE7] uppercase">
              {t("contact.title")}
            </h1>
          </Reveal>
        </div>
      </section>

      <section className="order-4 py-16 px-6">
        <div className="max-w-7xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-10">
          <Reveal>
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-full bg-[#FFFBEA] flex items-center justify-center text-[#6B5CE7]">
                <MapPin size={20} />
              </div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#6B5CE7] font-medium">{t("contact.adresseLabel")}</p>
              <p className="font-serif text-2xl text-[#1A1528]">{t("contact.adresseValue")}</p>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-full bg-[#FFFBEA] flex items-center justify-center text-[#6B5CE7]">
                <Phone size={20} />
              </div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#6B5CE7] font-medium">{t("contact.contactLabel")}</p>
              <a href="tel:+33789335568" data-testid="contact-phone" className="font-serif text-xl text-[#1A1528] hover:text-[#6B5CE7] block">+33 7 89 33 55 68</a>
              <a href="mailto:evara.monaco@gmail.com" data-testid="contact-email-link" className="font-serif text-lg text-[#1A1528] hover:text-[#6B5CE7] block break-all">evara.monaco@gmail.com</a>
              <div className="flex gap-3 pt-2">
                <a href="https://instagram.com" target="_blank" rel="noreferrer" data-testid="contact-instagram" className="w-10 h-10 rounded-full border border-[#1A1528]/15 flex items-center justify-center text-[#1A1528] hover:bg-[#6B5CE7] hover:text-white hover:border-transparent transition-all">
                  <Instagram size={16} />
                </a>
                <a href="https://linkedin.com" target="_blank" rel="noreferrer" data-testid="contact-linkedin" className="w-10 h-10 rounded-full border border-[#1A1528]/15 flex items-center justify-center text-[#1A1528] hover:bg-[#6B5CE7] hover:text-white hover:border-transparent transition-all">
                  <Linkedin size={16} />
                </a>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.2}>
            <div className="space-y-3">
              <div className="w-12 h-12 rounded-full bg-[#FFFBEA] flex items-center justify-center text-[#6B5CE7]">
                <Clock size={20} />
              </div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#6B5CE7] font-medium">{t("contact.horairesLabel")}</p>
              <div className="font-serif text-base text-[#1A1528] space-y-1">
                {horaires.map((h, i) => <p key={i}>{h}</p>)}
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* CALENDLY section — moved right below hero */}
      <section id="rdv" className="order-2 py-20 px-6 bg-[#FFFBEA] scroll-mt-24">
        <div className="max-w-5xl mx-auto text-center mb-12">
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-[#1A1528]">{t("contact.rdvTitle")}</h2>
            <p className="mt-5 text-[#4A4558] font-light text-base md:text-lg max-w-3xl mx-auto">{t("contact.rdvDesc")}</p>
          </Reveal>
        </div>

        <div className="max-w-5xl mx-auto rounded-3xl bg-white shadow-xl overflow-hidden border border-[#F5E6A3]/60">
          <div
            key={lang}
            data-testid="calendly-widget"
            className="calendly-inline-widget"
            data-url={`${CALENDLY_URL}?primary_color=6B5CE7&hide_event_type_details=0&hide_gdpr_banner=1`}
            style={{ minWidth: "320px", height: "780px" }}
          />
        </div>
      </section>

      {/* FORM section */}
      <section className="order-3 py-20 px-6 bg-white">
        <form onSubmit={onSubmit} data-testid="contact-form" className="max-w-4xl mx-auto bg-white rounded-3xl p-8 md:p-12 shadow-xl space-y-7 border border-[#F5E6A3]/40">
          <Reveal>
            <h3 className="font-serif text-3xl md:text-4xl font-bold text-[#6B5CE7] text-center pb-2">
              {t("contact.formIntro")}
            </h3>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#6B5CE7] mb-2">{t("contact.formNom")}</label>
              <input required value={form.nom} onChange={(e) => update("nom", e.target.value)} data-testid="contact-nom" className="w-full px-5 py-3.5 rounded-full border-2 border-[#F5E6A3] focus:border-[#6B5CE7] outline-none" />
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#6B5CE7] mb-2">{t("contact.formEmail")}</label>
              <input required type="email" value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="contact-email-input" className="w-full px-5 py-3.5 rounded-full border-2 border-[#F5E6A3] focus:border-[#6B5CE7] outline-none" />
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#6B5CE7] mb-2">{t("contact.formTel")}</label>
            <input type="tel" value={form.telephone} onChange={(e) => update("telephone", e.target.value)} data-testid="contact-phone-input" className="w-full px-5 py-3.5 rounded-full border-2 border-[#F5E6A3] focus:border-[#6B5CE7] outline-none" />
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#6B5CE7] mb-3">{t("contact.formType")}</p>
            <div className="flex gap-3 flex-wrap">
              {[t("contact.typePro"), t("contact.typePart")].map((opt) => (
                <button
                  key={opt}
                  type="button"
                  onClick={() => update("type_client", opt)}
                  data-testid={`contact-type-${opt.toLowerCase()}`}
                  className={`px-6 py-2.5 rounded-full text-sm transition-all ${
                    form.type_client === opt ? "bg-[#6B5CE7] text-white" : "bg-white border-2 border-[#F5E6A3] text-[#1A1528] hover:border-[#6B5CE7]"
                  }`}
                >
                  {opt}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#6B5CE7] mb-2">{t("contact.formSecteur")}</label>
              <select value={form.secteur} onChange={(e) => update("secteur", e.target.value)} data-testid="contact-secteur" className="w-full px-5 py-3.5 rounded-full border-2 border-[#F5E6A3] focus:border-[#6B5CE7] outline-none bg-white">
                <option value="">{t("contact.formSecteurPlaceholder")}</option>
                {secteurs.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="block text-xs uppercase tracking-[0.2em] text-[#6B5CE7] mb-2">{t("contact.formBudget")}</label>
              <select value={form.budget} onChange={(e) => update("budget", e.target.value)} data-testid="contact-budget" className="w-full px-5 py-3.5 rounded-full border-2 border-[#F5E6A3] focus:border-[#6B5CE7] outline-none bg-white">
                <option value="">{t("contact.formBudgetPlaceholder")}</option>
                {budgets.map((b) => <option key={b} value={b}>{b}</option>)}
              </select>
            </div>
          </div>

          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-[#6B5CE7] mb-3">{t("contact.formServices")}</p>
            <div className="flex flex-wrap gap-3">
              {servicesList.map((s) => {
                const active = form.services.includes(s);
                return (
                  <button
                    type="button"
                    key={s}
                    onClick={() => toggleService(s)}
                    data-testid={`contact-service-${s.toLowerCase().replace(/[^a-z]/g, "-")}`}
                    className={`px-5 py-2.5 rounded-full text-sm transition-all ${
                      active ? "bg-[#6B5CE7] text-white" : "bg-white border-2 border-[#F5E6A3] text-[#1A1528] hover:border-[#6B5CE7]"
                    }`}
                  >
                    {s}
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#6B5CE7] mb-2">{t("contact.formSource")}</label>
            <select value={form.source} onChange={(e) => update("source", e.target.value)} data-testid="contact-source" className="w-full px-5 py-3.5 rounded-full border-2 border-[#F5E6A3] focus:border-[#6B5CE7] outline-none bg-white">
              <option value="">{t("contact.formSourcePlaceholder")}</option>
              {sources.map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.2em] text-[#6B5CE7] mb-2">{t("contact.formMessage")}</label>
            <textarea rows={5} value={form.message} onChange={(e) => update("message", e.target.value)} data-testid="contact-message" className="w-full px-5 py-3.5 rounded-2xl border-2 border-[#F5E6A3] focus:border-[#6B5CE7] outline-none resize-none" />
          </div>

          <div className="flex justify-center pt-2">
            <button type="submit" disabled={loading} data-testid="contact-submit" className="btn-violet disabled:opacity-60">
              {loading ? t("common.sending") : t("contact.formSubmit")}
            </button>
          </div>
        </form>
      </section>

      {/* MAP — bottom of page */}
      <section className="order-5 px-6 pt-4 pb-16 bg-white">
        <div className="max-w-7xl mx-auto rounded-3xl overflow-hidden shadow-xl border border-[#F5E6A3]/60">
          <iframe
            title="Monaco / Monte-Carlo"
            data-testid="contact-map"
            src="https://www.google.com/maps?q=Monte-Carlo,+Monaco&output=embed"
            className="w-full h-96 md:h-[480px] border-0"
            loading="lazy"
          />
        </div>
      </section>
    </main>
  );
};

export default Contact;
